/**
 * Mirrors packages/amgcarracing_v2/lib/presentation/rive/rive_inputs.dart
 *
 * Car Racing is ONE artboard with ONE state machine; the whole bet
 * surface, race animation, and result popup live inside it. There is no
 * separate betting / popup / winlose artboard.
 */

export const Main = {
  file: 'carracer.riv',
  artboard: 'Main',
  stateMachine: 'MainStageMachine',
} as const;

/** Top-level state-machine inputs on the Main artboard. */
export const MainInputs = {
  // Number — final race position 1..32 (mapped from winning betItemId
  // via SPIN_MAPPING). Setting `Spin = 0` first then the real value
  // resets the animation each round.
  spin: 'Spin',
  // Number — winner highlight lane 1..8. Value = 378 - winningBetItemId.
  // Write `isEndHide = false` BEFORE setting this so the highlight shows.
  winning: 'Winning',
  // Number — multiplayer round-kickoff position (Flutter sets to 1.0).
  diceBowlPosition: 'DiceBowlPosition',
  // Trigger — multiplayer round-kickoff play signal.
  trigDiceBowlPlay: 'DiceBowlPlay',
  // Boolean — gates the "ending" panel visibility; false = show.
  isEndHide: 'isEndHide',
  // NOTE: the `isSoundOn` boolean input was REMOVED from MainStageMachine.
  // The artboard now emits its sound-cue events (Boost, StartSpin, CorrectClip,
  // Winning1..3, HilightBox, MouseClick) unconditionally, so there is no input
  // to set. Audibility is gated entirely by SoundManager (see audio/).
  // Boolean — mobile/desktop layout toggle (set by the bootstrap once).
  isMobile: 'isMobile',
} as const;

/**
 * Event names emitted by the Main state machine.
 * Mirrors `_handleRiveEvent` in
 *   packages/amgcarracing_v2/lib/presentation/screens/game_screen.dart:351-477
 * and `CarRacingRiveInputs.event*` constants in rive_inputs.dart.
 */
export const RiveEventNames = {
  // Bet area tapped. Property `betID` is 1-based (1..8), maps via
  // BET_ITEM_IDS[betID - 1]. Property `chipsAmt` is the currently-selected
  // chip value at the time of the click (mirrored back from the UI).
  betButton: 'BetButton',
  // CLEAR pressed inside Rive. Property `chipsAmt` may also be present.
  clear: 'evtClearClick',
  // PLAY / CONFIRM pressed inside Rive. Property `chipsAmt` may be present.
  play: 'evtButtonPlayClick',
  // Sound on/off toggle.
  soundToggle: 'evtSoundOnOff',
  // Background music on/off toggle.
  musicToggle: 'evtMusicOnOff',
  // Race animation finished — kicks off `_doEndingAnimation`.
  spinEnd: 'spinEnd',
  // Optional — emitted by some .riv builds when all controllers are ready.
  allControllersReady: 'allControllersReady',
} as const;

/**
 * History icon nested-input layout.
 *   path candidates: `Historyicon{1..10}` / `HistoryIcon{1..10}`
 *   number input name candidates at that path: `result` / `Result`
 *   value semantics: raw betItemId (370..377), 0.0 = empty slot.
 */
export const HistoryInputs = {
  pathCandidates: (i: number): string[] => [
    `Historyicon${i}`,
    `HistoryIcon${i}`,
  ],
  resultInputCandidates: ['result', 'Result'] as const,
};

/**
 * Per-bet-area text runs the Rive expects driven from the orchestrator.
 *   txtSubTotal{1..8}   — current player's wager on each area.
 *   txtAllTotal{1..8}   — all-players total per area (multiplayer only).
 *   txtTotalBet         — sum of player's own wagers.
 *   txtResultAmount     — winning amount displayed during ending animation.
 *   txtBalance          — player wallet balance.
 *   txtMinBet / txtMaxBet — table limits.
 */
export const TextRuns = {
  subTotal: (i: number): string => `txtSubTotal${i}`,
  allTotal: (i: number): string => `txtAllTotal${i}`,
  // Payout-odds labels txtOdd1..txtOdd8 (top-level Main-artboard text runs,
  // same block as txtSubTotal). Driven from the backend GameOdd1 list — see
  // RiveLayer.setAllOdds. e.g. txtOdd8 = "x2", txtOdd1 = "x40".
  odd: (i: number): string => `txtOdd${i}`,
  totalBet: 'txtTotalBet',
  resultAmount: 'txtResultAmount',
  balance: 'txtBalance',
  minBet: 'txtMinBet',
  maxBet: 'txtMaxBet',
} as const;

/**
 * MainWinLose.riv overlay — a SECOND artboard on its own stacked canvas.
 *
 * The base carracer.riv has no StartBet / NoMoreBet bet-window popups and no
 * "YOU WIN / YOU LOSE" panel, so they're driven from this separate full-screen
 * transparent overlay (added 2026-06-16). This MainWinLose.riv is BYTE-IDENTICAL
 * to amg_baccarat_pixi's copy (verified by sha256), so baccarat's proven
 * contract applies verbatim — artboard "Button", state machine "animationManager":
 *
 *   - StartBet / NoMoreBet popups: set BetPanelNumber (1 = StartBet, 2 = NoMoreBet)
 *     then fire BetPanelTrigger. The artboard emits a `StartBet` / `NoMoreBet`
 *     Rive event when the popup plays.
 *   - YOU WIN / YOU LOSE popup: set the txtWonAmount text run + YouWinLose number
 *     (1 = win, 2 = lose) then fire YouWinLoseTrigger. The artboard emits an
 *     `evtYouWinEnd` / `evtYouLoseEnd` Rive event when the popup finishes.
 */
export const WinLose = {
  file: 'MainWinLose.riv',
  artboard: 'Button',
  stateMachine: 'animationManager',
} as const;

export const WinLoseInputs = {
  /** Number selecting the bet popup: 1 = StartBet, 2 = NoMoreBet. */
  betPanelNumber: 'BetPanelNumber',
  /** Fire after setting betPanelNumber to play the selected bet popup. */
  betPanelTrigger: 'BetPanelTrigger',
  /** Number selecting the result popup: 1 = win, 2 = lose. */
  youWinLose: 'YouWinLose',
  /** Fire after setting youWinLose + txtWonAmount to play the result popup. */
  youWinLoseTrigger: 'YouWinLoseTrigger',
  /** Text run for the won/lost amount shown in the result popup. */
  txtWonAmount: 'txtWonAmount',
} as const;

/** BetPanelNumber values for the StartBet / NoMoreBet popups. */
export const BetPanelState = {
  startBet: 1,
  noMoreBet: 2,
} as const;
